import {h} from 'preact';
import {Prompt} from 'react-router-dom';
import {trimFormData} from '@util/StringUtils';
import ProductImage from '@components/common/ProductImage';
import {InputFeedback} from '@components/common/Form';
import {Radio} from '@components/common/Form/Radio';
import {Textarea} from '@components/common/Form/Textarea';
import Button from '@components/common/Button';
import Icon from '@components/common/Icon';
import {Address} from '@components/common/Address';
import {PageContent, BackButton, CallRepairsLink, EquipmentModelDetail} from '../components';
import {Link} from 'react-router-dom';
import {Formik} from 'formik';

const styles = {
    msgContainer: 'flex flex-col justify-center text-center max-w-md mx-auto',
    msgHeading: 'flex self-center items-center text-lg md:text-xl font-normal leading-collapse py-2 px-4',
    msgActionList: 'mt-4 flex justify-center items-center'
};

const EquipmentInCart = () =>
    <div className={styles.msgContainer}>
        <h2 className={styles.msgHeading}>
            <Icon.ui className="mr-2 text-lg" center name="checkmark"/> This item is in your cart
        </h2>
        <p className="mt-1">A repair request for this is item is ready to be submitted.</p>
        <div className={styles.msgActionList}>
            <Link className="link-2" to={`/cart`}>Review your cart</Link>
            <span className="px-4">or</span>
            <Link className="link-2" to={`/checkout`}>Proceed with repair</Link>
        </div>
    </div>;

const RepairRequestUnavailable = props => {
    if ( props.requestStatus === 'CANCELED' || props.requestLineStatus === 'CANCELED' ) {
        return (
            <div className={styles.msgContainer}>
                <h2 className={styles.msgHeading + ' text-fail'}>
                    <Icon.ui className="mr-2 text-lg" center name="report-problem-triangle"/>
                    <span>A repair request for this item has been canceled</span>
                </h2>
                <p className="mt-4">
                    If you would like to submit a new repair for this item please contact our equipment repair services
                    by calling <CallRepairsLink />
                </p>
                <div className={styles.msgActionList}>
                    <Link className="button secondary"
                          to={`/repair-history/${props.requestId}`}>View canceled repair</Link>
                </div>
            </div>
        );
    }

    return (
        <div className={styles.msgContainer}>
                <h2 className={styles.msgHeading}>
                    This item is being repaired
                </h2>
                <p className="mt-4">A repair request for this is item has already been submitted.</p>
                <p className="mt-4">
                    If you would like to submit additional repairs for this item please contact our equipment repair services
                    by calling <CallRepairsLink />
                </p>
                <div className={styles.msgActionList}>
                    <Link className="button secondary"
                          to={`/open-repairs/${props.requestId}`}>View repair progress</Link>
                </div>
            </div>
    );
};

const RepairRequestForm = ( {equipmentItem, actions, history} ) => {
    let isPreventingNavigation = false;

    return (
        <Formik
            initialValues={{
                patientInjury: null,
                equipmentIssue: ''
            }}
            validate={formData => {

                const errors = {};
                const {equipmentIssue, patientInjury} = trimFormData( formData );
                if ( !equipmentIssue ) {
                    errors.equipmentIssue = 'A description for you equipment issue is required'
                }
                if ( !patientInjury ) {
                    errors.patientInjury = 'Please indicate if using this equipment resulted in injury'
                }

                // set navigation lock
                isPreventingNavigation = equipmentIssue.length > 0 || !!patientInjury;

                return errors;
            }}
            onSubmit={formData => {
                const {equipmentIssue, patientInjury} = trimFormData( formData );
                actions.addItem( {
                    serialId: equipmentItem.serialId,
                    injury: patientInjury === 'yes',
                    equipmentIssue
                } ).then( () => {
                    history.push( {
                        pathname: '/cart',
                        state: {justAdded: true}
                    } );
                } );
            }}
            render={formikBag =>
                <form
                    data-testid="request-repair-form"
                    className="flex-auto"
                    onSubmit={formikBag.handleSubmit}>
                    <Prompt when={isPreventingNavigation}
                            message={'Are you sure you want to leave without submitting your equipment questionnaire?'}/>
                    <div className="md:px-4 max-w-md mx-auto">
                        <h3>
                            To begin the repair process please answer a few questions to help our technicians
                            diagnose your equipment issues.
                        </h3>
                        <h4 className="mt-4 md:mt-8 max-w-sm leading-tight font-normal text-lg">
                            Did using this equipment result in an injury to the patient or person operating
                            device?
                        </h4>
                        <fieldSet>
                            <Radio label="No"
                                   name="patientInjury"
                                   onChange={formikBag.handleChange}
                                   value="no"
                                   inputClassName="mr-1"/>
                           <Radio label="Yes"
                                  name="patientInjury"
                                  onChange={formikBag.handleChange}
                                  value="yes"
                                  inputClassName="mr-1"/>
                          <InputFeedback
                              error={formikBag.touched.equipmentIssue && formikBag.errors.patientInjury}/>
                        </fieldSet>

                        <div>
                            <h4 className="mt-4 md:mt-8 max-w-sm leading-tight font-normal text-lg">Describe any
                                issues you are experiencing with your equipment.</h4>
                            <p className="mt-1 text-sm max-w-sm">
                                Remember to be as specific as possible about any noises, malfunctions or damage
                                to your equipment.</p>
                            <fieldset>
                                <label htmlFor="equipmentIssue">Equipment issue</label> <Textarea
                                id="equipmentIssue"
                                inputClassName="w-full"
                                value={formikBag.values.equipmentIssue}
                                error={formikBag.touched.equipmentIssue && formikBag.errors.equipmentIssue}
                                onChange={formikBag.handleChange}
                            />
                            </fieldset>

                            <div className="md:mt-12 flex justify-center">
                                <Button type="submit"
                                        fillMobile
                                        className="my-2 button"
                                        icon="shopping-cart-add"> Add to repairs cart </Button>
                            </div>
                        </div>
                    </div>
                </form>
            }/>
    );
};

export const EquipmentDetail = ( {equipmentItem, actions, ...props} ) =>
    <PageContent>
        <BackButton history={props.history}/>
        <div className="mt-8 flex flex-col md:flex-row">
            {equipmentItem.rmaDetail != null
                ? <RepairRequestUnavailable {...equipmentItem.rmaDetail} />
                : equipmentItem.isInCart
                    ? <EquipmentInCart/>
                    : <RepairRequestForm
                        equipmentItem={equipmentItem}
                        actions={actions}
                        history={props.history}/>
            }

            <div
                className="w-80 order-none md:order-first mt-12 md:mt-0 pt-8 md:pt-0 pr-8 border-t md:border-t-0 md:border-r border-solid border-grey-lightest">
                <h2 data-testid="equipment-detail-title"
                    className="block md:text-xl font-normal">{equipmentItem.itemName}</h2>
                <div className="my-4 text-center img-ratio-4x3">
                    <ProductImage className="pr-4"
                                  width="288"
                                  images={equipmentItem.images}
                                  kitName={equipmentItem.itemName}/>
                </div>
                <div className="">
                    <EquipmentModelDetail className="mt-2" {...equipmentItem} />

                    <h4 className="mt-4">Warranty information</h4>
                    <p>{equipmentItem.yearsCoverage} years beginning {equipmentItem.warrantyCoverageStart}</p>

                    <h4 className="mt-4">Office address / Ownership</h4>
                    <Address {...equipmentItem.address}/>
                </div>

                {/*<div className="mt-12">
                    <h3 className="text-lg">Trouble Shooting Guides</h3>
                    <p>for {equipmentItem.itemName}</p>
                    <ul className="mt-2 linkList">
                        <li><a href="/">Lorem ipsum dolor sit</a></li>
                        <li><a href="/">Consectetur adipisicing elit</a></li>
                        <li><a href="/">Consectetur corporis cum dolor</a></li>
                        <li><a href="/">Amet doloribus eaque eos eum</a></li>
                        <li><a href="/">Ipsa labore laborum libero magni</a></li>
                        <li><a href="/">Molestiae neque officiis ratione</a></li>
                        <li><a href="/">Fugiat in ut vero, voluptas</a></li>
                    </ul>
                </div>*/}
            </div>
        </div>
    </PageContent>;
